/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { SecurityHelper } from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import * as types from '../constants';
import {
    getNewId,
    nightsInRange,
    isVehicle,
    isWatercraft,
    equipmentTypes,
} from './utilsNew';

export const createGroupMember = (startDate, endDate) => {
    const groupMember = {};
    if (startDate) {
        groupMember.start_date = startDate;
    }
    if (endDate) {
        groupMember.end_date = endDate;
    }
    return groupMember;
};

export const setGroupDetails = (groupSize, launchDetail) => {
    const groupMembers = [];
    const { entryDate, exitDate } = launchDetail;

    for (let i = 0; i < groupSize; i++) {
        const groupMember = createGroupMember(entryDate, exitDate);
        groupMembers.push(groupMember);
    }
    return groupMembers;
};

export const convertIssuanceToGroupMembersObj = (groupInfo) => {
    const result = [];
    if (groupInfo) {
        for (const member of groupInfo) {
            result.push({
                id: member.id || getNewId(),
                fName: member.first_name || '',
                mName: member.middle_name || '',
                lName: member.last_name || '',
                startDate: member.start_date
                    ? gnoment.utc(member.start_date).startOf('day')
                    : member.start_date,
                endDate: member.end_date
                    ? gnoment.utc(member.end_date).startOf('day')
                    : member.end_date,
                passholderId: member.passholder_id,
                passId: member?.pass_id, // WARNING - this can also be used in lieu of passholder id in some cases
                passholderType: member.pass_type,
                remark: member.remark || '',
                isAdult: member.is_adult || (!member.is_child && !member.is_dog), // default to adult if nothing is set
                isChild: member.is_child,
                isDog: member.is_dog,
                isLeader: member.is_leader,
                age: member.age,
                birthdate: member.date_of_birth,
                discountId: member?.discount_id,
            });
        }
    }
    return result;
};

export const convertIssuanceToGroupDetailObj = (issuance, groupDetail) => {
    // this takes issuance fields and builds an object used for local representation
    // this is the problem method, that is cross referenced.
    // parameter changes are the problem.
    const alternateLeaders = issuance.alt_group_leaders;
    const permitHolder = issuance.permit_holder;
    const permitHolderAddress = issuance.permit_holder_address;
    const result = { ...groupDetail };

    const permitHolderValid =
        permitHolder.first_name &&
        permitHolder.last_name &&
        permitHolder.email &&
        permitHolder.phone;
    const permitHolderAddressValid =
        permitHolderAddress.street_address1 &&
        permitHolderAddress.city &&
        permitHolderAddress.country_code &&
        permitHolderAddress.state_code &&
        permitHolderAddress.postal_code;

    if (permitHolderValid && permitHolderAddressValid) {
        // get the 'groupLeader' from the Individual/Address objects
        result.groupLeader = {
            first_name: permitHolder.first_name,
            last_name: permitHolder.last_name,
            email: permitHolder.email,
            cell_phone: permitHolder.phone,
            user_id: permitHolder.user_id,
            home_address: {
                address1: permitHolderAddress.street_address1,
                address2: permitHolderAddress.street_address2,
                city: permitHolderAddress.city,
                country: permitHolderAddress.country_code,
                state: permitHolderAddress.state_code,
                zip_code: permitHolderAddress.postal_code,
            },
        };
    } else {
        // get the group leader info from current session account
        result.groupLeader = SecurityHelper.getAccount();
    }

    if (alternateLeaders) {
        const altLeaders = [];
        for (let index = 0; index < alternateLeaders.length; index++) {
            const leader = alternateLeaders[index];
            const altLeader = {
                key: getNewId(),
                first_name: leader.first_name || '',
                mName: '',
                last_name: leader.last_name || '',
                phone: '',
                email: leader.email,
                is_verified: leader.is_verified,
                is_declined: leader.is_declined,
            };
            altLeaders.push(altLeader);
        }
        result.altLeaders = altLeaders;
    }
    return result;
};

export const convertIssuanceToVehiclesObj = (equipments, launchDetail, entrances) => {
    const result = [];

    // TODO: next two are dummy conditional statements to bypass the lint error (never used).
    // We can consider removing launchDetail and entrances params here and from all calls in the other files in the future.
    if (!launchDetail) {
        // dummy block
    }
    if (entrances.length === 0) {
        // dummy block
    }

    if (equipments) {
        for (const e of equipments) {
            if (isVehicle(e.type)) {
                result.push(e);
            }
        }
    }

    return result;
};

export const convertIssuanceToWatercraftObj = (equipments) => {
    const result = [];
    if (equipments) {
        for (const e of equipments) {
            if (e.qty > 0 && isWatercraft(e.type)) {
                result.push({
                    id: getNewId(),
                    type: e.type,
                    amount: e.qty,
                });
            }
        }
    }

    return result;
};

export const convertIssuanceToAnimalObj = (animals) => {
    const result = [];
    if (animals) {
        for (const a of animals) {
            if (a.amount > 0) {
                result.push({
                    id: getNewId(),
                    type: a.type.toString(),
                    amount: a.amount,
                });
            }
        }
    }

    return result;
};

export const convertIssuanceToLaunchDetailObj = (issuance) => {
    const item = {
        entryLocationId: issuance.entry_location_id,
        exitLocationId: issuance.exit_location_id,
        isExtended: issuance.is_extended,
        launchTime: issuance.launch_time,
    };
    if (issuance.start_date) {
        item.entryDate = gnoment(issuance.start_date).utc();
    }
    if (issuance.end_date) {
        item.exitDate = gnoment(issuance.end_date).utc();
    }
    return item;
};

export const convertIssuanceToIssueStationDetailObj = (issueStationId) => {
    return {
        selectedStationId: issueStationId,
    };
};

export const convertIssuanceToCampsitePreferenceObj = (campsitePreferences) => {
    const prefs = {};
    if (campsitePreferences) {
        for (const key of Object.keys(campsitePreferences)) {
            const prefKey = gnoment(key).utc().format(types.MONTH_DAY_YEAR_FORMAT);
            prefs[prefKey] = campsitePreferences[key];
        }
    }
    return prefs;
};

export const convertIssuanceToGuidesObj = (guides) => {
    if (!guides) {
        return [];
    }

    return guides.map((guide) => ({
        first_name: guide.first_name,
        last_name: guide.last_name,
        org_name: guide.org_name,
        license_expiration_date: guide.expiration_date,
        license_number: guide.passholder_id,
        email: guide.email,
        phone: guide.phone,
    }));
};

export function fetchGroupDetails(groupDetail) {
    const groupDetailInfo = {};
    if (groupDetail.groupLeaderType === 'primary-leader') {
        groupDetailInfo.group_leader_id = SecurityHelper.getAccount().account_id;
    }

    if (groupDetail.altLeaders) {
        groupDetailInfo.alt_group_leaders = groupDetail.altLeaders;
        const altGroupLeaders = [];
        for (let index = 0; index < groupDetail.altLeaders.length; index++) {
            const leader = groupDetail.altLeaders[index];
            altGroupLeaders.push({
                email: leader.email,
                is_verified: leader.is_verified,
            });
        }
        groupDetailInfo.alt_group_leaders = altGroupLeaders;
    }
    return groupDetailInfo;
}

export function fetchEquipments(watercrafts, snowmobiles, vehicles) {
    const equipmentInfo = {
        equipments: [],
    };

    watercrafts.forEach((c) =>
        equipmentInfo.equipments.push({
            type: parseInt(c.type, 10),
            qty: parseInt(c.amount, 10),
        })
    );

    if (vehicles) {
        equipmentInfo.equipments.push(...vehicles);
    }

    if (snowmobiles) {
        equipmentInfo.equipments.push({
            type: equipmentTypes.get('Snowmobile'),
            qty: parseInt(snowmobiles, 10),
        });
    }

    equipmentInfo.equipments = equipmentInfo.equipments.filter(
        ({ type, qty }) => !isNaN(type) && !isNaN(qty)
    );
    return equipmentInfo;
}

export function fetchAnimals(animals) {
    const animalsObj = {
        animals: [],
    };
    if (animals) {
        animals.forEach((c) => {
            animalsObj.animals.push({
                type: parseInt(c.type, 10),
                amount: parseInt(c.amount, 10),
            });
        });
    }
    return animalsObj;
}

export function fetchGuides(guides, account, isCommerciallyGuided) {
    // converts our guide objects into what GuideDetails expects
    const guidesObj = {
        guides: [],
    };

    const orgName = account.company_name;
    if (guides) {
        if (account.is_commercial_account) {
            guides.forEach((g) => {
                guidesObj.guides.push({
                    org_name: orgName,
                    first_name: g.first_name,
                    last_name: g.last_name,
                    passholder_id: g.license_number,
                    expiration_date: g.license_expiration_date,
                });
            });
        } else if (isCommerciallyGuided || guides.length > 0) {
            // non commercial account being guided
            guides.forEach((g) => {
                guidesObj.guides.push({
                    first_name: g.first_name,
                    last_name: g.last_name,
                    phone: g.phone,
                    email: g.email,
                    org_name: g.org_name,
                    passholder_id: g.license_number,
                    expiration_date: g.license_expiration_date,
                });
            });
        }
    }
    return guidesObj;
}

export function fetchMembers(groupMembers) {
    const memberInfo = {
        group_info: [],
    };
    groupMembers.forEach((m) => {
        let startDate = m.startDate;
        let endDate = m.endDate;
        if (gnoment.isGnoment(m.startDate)) {
            startDate = m.startDate.utc(true).startOf('day');
        }
        if (gnoment.isGnoment(m.endDate)) endDate = m.endDate.utc(true).startOf('day');
        memberInfo.group_info.push({
            first_name: m.fName,
            middle_name: m.mName,
            last_name: m.lName,
            remark: m.remark,
            start_date: startDate,
            end_date: endDate,
            pass_type: m.passholderType,
            passholder_id: m.passholderId,
        });
    });
    return memberInfo;
}

export function fetchCampsitePreferences(campsitePreferences, entryDate, exitDate) {
    const campsitePreferencesInfo = {
        campsite_preferences: {},
    };
    const nightDates = nightsInRange(entryDate, exitDate);
    if (campsitePreferences) {
        nightDates.forEach((nightDate) => {
            const displayDate = gnoment(nightDate)
                .utc()
                .format(types.MONTH_DAY_YEAR_FORMAT);
            const pref = campsitePreferences[displayDate];
            const momentDate = gnoment(nightDate);
            if (pref) {
                campsitePreferencesInfo.campsite_preferences[
                    momentDate.utcOffset(0).startOf('day').toISOString()
                ] = pref;
            }
        });
    }
    return campsitePreferencesInfo;
}

export const getResizedGroupInfo = (issuance, newSize) => {
    const groupMembersArray = issuance.group_info;
    if (newSize > groupMembersArray?.length) {
        // if more group members added, add some empty for use
        return [
            ...groupMembersArray,
            ...Array(newSize - groupMembersArray.length)
                .fill()
                .map(() => ({
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                    is_adult: true,
                    start_date: issuance.start_date,
                    end_date: issuance.end_date,
                })),
        ];
    }
    if (newSize < groupMembersArray?.length) {
        return groupMembersArray.slice(0, newSize);
    }
    return [...groupMembersArray];
};

export const modifyIssuanceGroupSize = (issuance, newSize) => {
    const updatedReservation = {
        ...issuance,
        group_info: getResizedGroupInfo(issuance, newSize),
        group_size: newSize,
    };
    return updatedReservation;
};
